import React, { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const EliminarSolicitudModal = ({ 
  isOpen, 
  onClose, 
  solicitud, 
  onConfirm,
  tipo = 'lenceria' // 'lenceria', 'insumos', 'vertical'
}) => {
  const [motivo, setMotivo] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    if (!motivo.trim()) {
      return;
    }

    setIsDeleting(true);
    try {
      await onConfirm(solicitud, motivo.trim());
      handleClose();
    } catch (error) {
      console.error('Error al eliminar solicitud:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setMotivo('');
    onClose();
  };

  if (!solicitud) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Eliminar Solicitud
          </DialogTitle>
          <DialogDescription>
            Esta acción no se puede deshacer. La solicitud será eliminada permanentemente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Información de la solicitud */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="font-medium text-gray-700">Portafolio:</span>
                <p className="text-gray-900">{solicitud.edificio?.nombre || 'N/A'}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Estado:</span>
                <p className="text-gray-900">{solicitud.estado || 'N/A'}</p>
              </div>
              {solicitud.nombrePropiedad && (
                <div>
                  <span className="font-medium text-gray-700">Propiedad:</span>
                  <p className="text-gray-900">{solicitud.nombrePropiedad}</p>
                </div>
              )}
              <div>
                <span className="font-medium text-gray-700">Fecha de entrega:</span>
                <p className="text-gray-900">
                  {new Date(solicitud.fechaEntrega).toLocaleDateString('es-ES')}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Total items:</span>
                <p className="text-gray-900">{solicitud.total_solicitado || solicitud.items?.length || 0}</p>
              </div>
              {solicitud.peso_total && (
                <div>
                  <span className="font-medium text-gray-700">Peso total:</span>
                  <p className="text-gray-900">{solicitud.peso_total}g</p>
                </div>
              )}
            </div>
          </div>

          {/* Campo de motivo */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Motivo de eliminación <span className="text-red-500">*</span>
            </label>
            <Textarea
              placeholder="Describe el motivo por el cual se elimina esta solicitud..."
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={4}
              className="resize-none"
            />
            {!motivo.trim() && (
              <p className="text-xs text-gray-500">
                El motivo es obligatorio para eliminar la solicitud
              </p>
            )}
          </div>

          {/* Advertencia */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>
                Al eliminar esta solicitud, se perderá toda la información asociada 
                y no podrá ser recuperada.
              </span>
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!motivo.trim() || isDeleting}
          >
            {isDeleting ? 'Eliminando...' : 'Eliminar Solicitud'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EliminarSolicitudModal;
